import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EngagementSection = () => {
  const [activeTab, setActiveTab] = useState('independence');

  const engagementPrinciples = [
    {
      id: 'independence',
      title: 'Independence & Objectivity',
      icon: 'Shield',
      description: 'Maintaining complete independence from all parties to ensure unbiased valuations',
      details: [
        'No financial interest in the subject company',
        'Independent verification of all data sources',
        'Objective application of valuation methodologies',
        'Clear disclosure of any potential conflicts'
      ],
      assurance: 'We maintain strict independence standards that exceed professional requirements, ensuring our valuations are completely objective and defensible.'
    },
    {
      id: 'confidentiality',
      title: 'Confidentiality & Security',
      icon: 'Lock',
      description: 'Protecting sensitive business information with enterprise-grade security measures',
      details: [
        'Secure document portal for information sharing',
        'Non-disclosure agreements with all team members',
        'Encrypted data storage and transmission',
        'Limited access on need-to-know basis'
      ],
      assurance: 'Your confidential information is protected by bank-level security protocols and strict confidentiality procedures throughout the engagement.'
    },
    {
      id: 'admissibility',
      title: 'Court Admissibility',
      icon: 'Scale',
      description: 'Ensuring all valuations meet legal standards for court proceedings and regulatory compliance',
      details: [
        'Compliance with legal evidence standards',
        'Comprehensive documentation of methodology',
        'Expert witness testimony capability',
        'Peer review by senior professionals'
      ],
      assurance: 'Our reports are designed to withstand legal scrutiny in court proceedings.'
    },
    {
      id: 'quality',
      title: 'Quality Assurance',
      icon: 'CheckCircle',
      description: 'Multi-layer quality control process ensuring accuracy and completeness',
      details: [
        'Independent technical review process',
        'Senior partner oversight and approval',
        'Comprehensive quality control checklist',
        'Client review and feedback incorporation'
      ],
      assurance: 'Every valuation undergoes rigorous quality assurance to ensure accuracy, completeness, and professional presentation standards.'
    }
  ];

  const pricingStructure = [
    {
      category: 'Calculation Reports',
      range: '$3,000 - $8,000',
      factors: [
        'Company size and complexity',
        'Industry specialization required',
        'Data availability and quality',
        'Timeline requirements'
      ],
      typical: '$5,000',
      timeline: '1-2 weeks'
    },
    {
      category: 'Summary Reports',
      range: '$5,000 - $15,000',
      factors: [
        'Scope of analysis required',
        'Number of valuation approaches',
        'Market research complexity',
        'Presentation requirements'
      ],
      typical: '$12,000',
      timeline: '2-3 weeks'
    },
    {
      category: 'Comprehensive Reports',
      range: '$9,000 - $25,000',
      factors: [
        'Full scope analysis and documentation',
        'Multiple methodology application',
        'Extensive market research',
        'Court-admissible documentation'
      ],
      typical: '$20,000',
      timeline: '3-4 weeks'
    }
  ];

  const complexityFactors = [
    {
      factor: 'Business Structure',
      simple: 'Single entity, straightforward operations',
      complex: 'Multiple entities, complex ownership structures',
      impact: 'Low to High'
    },
    {
      factor: 'Financial Complexity',
      simple: 'Clean financial statements, standard accounting',
      complex: 'Complex transactions, multiple adjustments needed',
      impact: 'Medium to High'
    },
    {
      factor: 'Industry Factors',
      simple: 'Established industry with clear comparables',
      complex: 'Emerging sector, limited market data',
      impact: 'Low to Medium'
    },
    {
      factor: 'Geographic Scope',
      simple: 'Single jurisdiction operations',
      complex: 'Multi-jurisdictional, regulatory complexity',
      impact: 'Medium to High'
    },
    {
      factor: 'Timeline Requirements',
      simple: 'Standard timeline (3-4 weeks)',
      complex: 'Expedited delivery (2-3 weeks)',
      impact: 'Medium'
    }
  ];

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Engagement Framework
          </h2>
          <p className="text-xl text-text-secondary leading-relaxed">
            Our engagement approach ensures professional standards, transparent pricing, and complete client confidence throughout the valuation process.
          </p>
        </div>

        {/* Engagement Principles */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Professional Standards</h3>

          {/* Principle Tabs */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {engagementPrinciples?.map((principle) => (
              <button
                key={principle?.id}
                onClick={() => setActiveTab(principle?.id)}
                className={`p-6 rounded-xl border-2 transition-all duration-300 text-center ${activeTab === principle?.id
                  ? 'border-accent bg-white shadow-brand'
                  : 'border-border bg-white hover:border-accent/50'
                  }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${activeTab === principle?.id ? 'bg-accent text-white' : 'bg-muted text-text-secondary'
                  }`}>
                  <Icon name={principle?.icon} size={20} />
                </div>
                <h4 className="font-semibold text-primary text-sm">{principle?.title}</h4>
              </button>
            ))}
          </div>

          {/* Active Principle Details */}
          <div className="bg-white rounded-2xl p-8 border border-border">
            {engagementPrinciples?.map((principle) => (
              activeTab === principle?.id && (
                <div key={principle?.id} className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center">
                        <Icon name={principle?.icon} size={28} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-primary">{principle?.title}</h4>
                        <p className="text-text-secondary">{principle?.description}</p>
                      </div>
                    </div>

                    <h5 className="font-semibold text-primary mb-4">Key Components:</h5>
                    <ul className="space-y-3">
                      {principle?.details?.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                          <span className="text-text-primary">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-muted rounded-xl p-6">
                    <h5 className="font-semibold text-primary mb-4">Our Commitment</h5>
                    <p className="text-text-secondary leading-relaxed mb-6">{principle?.assurance}</p>

                    <div className="bg-white p-4 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="Award" size={20} className="text-accent" />
                        <div>
                          <p className="font-medium text-primary">Professional Guarantee</p>
                          <p className="text-sm text-text-secondary">
                            Backed by professional liability insurance and industry certifications
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Pricing Transparency */}
        {/* <div className="mb-16">
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Transparent Pricing</h3>
          
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {pricingStructure?.map((tier, index) => (
              <div key={index} className="bg-white rounded-xl p-8 border border-border hover:shadow-brand transition-shadow duration-300">
                <h4 className="text-xl font-semibold text-primary mb-4">{tier?.category}</h4>
                
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-accent mb-2">{tier?.range}</div>
                  <div className="text-sm text-text-secondary">Typical: {tier?.typical}</div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Timeline</span>
                    <span className="font-medium text-primary">{tier?.timeline}</span>
                  </div>
                </div>

                <h5 className="font-semibold text-primary mb-3">Pricing Factors:</h5>
                <ul className="space-y-2">
                  {tier?.factors?.map((factor, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <Icon name="Dot" size={12} className="text-accent flex-shrink-0 mt-2" />
                      <span className="text-sm text-text-secondary">{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-accent/5 border border-accent/20 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <Icon name="Info" size={24} className="text-accent flex-shrink-0 mt-1" />
              <div>
                <h5 className="font-semibold text-accent mb-2">Fixed Fee Engagements</h5>
                <p className="text-text-secondary">
                  We provide fixed-fee quotes after initial consultation to ensure complete cost transparency. 
                  No surprises or scope creep - you'll know the exact investment upfront.
                </p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Complexity Factors */}
        <div>
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Complexity Assessment</h3>

          <div className="bg-white rounded-2xl p-8 border border-border">
            <p className="text-text-secondary mb-8 text-center max-w-3xl mx-auto">
              Understanding the factors that influence engagement complexity helps set appropriate expectations for timeline and investment.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 font-semibold text-primary">Factor</th>
                    <th className="text-left py-4 px-4 font-semibold text-primary">Simple</th>
                    <th className="text-left py-4 px-4 font-semibold text-primary">Complex</th>
                    <th className="text-center py-4 px-4 font-semibold text-primary">Cost Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {complexityFactors?.map((item, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/30 transition-colors duration-200">
                      <td className="py-4 px-4 font-medium text-primary">{item?.factor}</td>
                      <td className="py-4 px-4 text-sm text-text-secondary">{item?.simple}</td>
                      <td className="py-4 px-4 text-sm text-text-secondary">{item?.complex}</td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${item?.impact?.includes('High') ? 'bg-warning/10 text-warning' :
                          item?.impact?.includes('Medium') ? 'bg-accent/10 text-accent' :
                            'bg-success/10 text-success'
                          }`}>
                          {item?.impact}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 text-center">
              {/* <Button
                variant="default"
                size="lg"
                iconName="Calculator"
                iconPosition="left"
              >
                Get Custom Quote
              </Button> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngagementSection;